import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  loadSelectedAccount,
  loadSelectedAccountFailure,
  loadSelectedAccountSuccess,
} from '../../actions/accountActions';
import { ENSURE_SELECTED_ACCOUNT, INITIAL_AUTHENTICATION, LOAD_SELECTED_ACCOUNT } from '../../actions/actionTypes';
import { Action } from '../../actions/commonActions';
import { clearGridFilter } from '../../actions/gridActions';
import { clearSims } from '../../actions/simActions';
import { clearSimSubmissions } from '../../actions/simSubmissionActions';
import { clearUserCommandTypes } from '../../actions/userCommandTypeActions';
import { loadUserRoles } from '../../actions/userRoleActions';
import * as accountApi from '../../api/accountApi';
import { Loadable } from '../../models';
import { AccountDetail, AccountLoadParameters } from '../../models/account';
import { selectAccount } from '../../selectors/filter';

const log = getLogger('simmanagement.sagas.account.index');

/**
 * Ensure that the selected account is present in the store and will initiate a server load if not.
 * @param action The action that invoked the saga.
 */
export function* ensureSelectedAccountSaga(action: Action<AccountLoadParameters>) {
  if (action.payload) {
    const account: Loadable<AccountDetail> = yield select(selectAccount);
    const param = action.payload;
    if (account.id !== param.accountId || (account.expiresOn && account.expiresOn < new Date())) {
      yield put(loadSelectedAccount(param));
    }
  }
}

/**
 * Load the selected account.
 * @param action The action that invoked the saga.
 */
export function* loadSelectedAccountSaga(action: Action<AccountLoadParameters>) {
  const payload = action.payload;
  if (payload) {
    const oldAccount: Loadable<AccountDetail> = yield select(selectAccount);
    if (oldAccount.id !== payload.accountId) {
      yield put(clearGridFilter('simsList'));
      yield put(clearSims());
      yield put(clearSimSubmissions());
      yield put(clearUserCommandTypes());
    }

    try {
      const newAccount: AccountDetail = yield call(accountApi.getById, payload);
      yield put(loadSelectedAccountSuccess(newAccount));
      yield put(
        loadUserRoles({
          companyId: payload.companyId,
          accountId: payload.accountId,
        })
      );
    } catch (error: any) {
      yield put(loadSelectedAccountFailure(payload.accountId, error));
    }
  }
}

/*
 * Initialize all sagas related to accounts
 */
export function* initAccountSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(ENSURE_SELECTED_ACCOUNT, ensureSelectedAccountSaga);
  yield takeLatest(LOAD_SELECTED_ACCOUNT, loadSelectedAccountSaga);
}
